import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";

import { useAuth } from "../../hooks/useAuth";
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import FeatureTrialModal from "./FeatureTrialModal";
import { searchPanel } from "../../api/requests/search-requests";
import {
    GROUP_ACTION,
    GROUP_LABELS,
    GROUP_PAGE,
    buildSearchTargets,
    filterByRoles,
    searchTargets,
} from "../../config/searchTargets";

const NAVY = "#0A316E";
const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

/** Backend'den gelen kayıt tipleri: başlık ve ikon karşılıkları. */
const RECORD_META = {
    ACCOUNT: { label: "Cariler", icon: "briefcase" },
    COMPANY: { label: "Firmalar", icon: "grid" },
    USER: { label: "Kullanıcılar", icon: "user" },
};
const RECORD_ORDER = ["ACCOUNT", "COMPANY", "USER"];
const RECENT_KEY = "tc.globalSearch.recent";
const RECENT_LIMIT = 4;

const readRecent = () => {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.slice(0, RECENT_LIMIT) : [];
    } catch {
        return [];
    }
};

const writeRecent = (ids) => {
    try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, RECENT_LIMIT)));
    } catch {
        /* özel sekmede yazma kapalı olabilir; arama yine çalışır */
    }
};

const styles = `
  .global-search { position: relative; display: flex; align-items: center; }
  .global-search .gs-toggle {
    display: inline-flex; align-items: center; justify-content: center;
    background: none; border: 0; padding: 0 .75rem; height: 70px; color: ${NAVY};
  }
  .global-search .gs-toggle:hover { color: #071f47; }

  /* Açılırken genişlik büyür: sağdaki blok genişleyince duyuru şeridi kendi
     flex-shrink'i ile daralır, aradaki butonlar sola kayar. */
  .global-search .gs-field {
    display: flex; align-items: center; gap: .4rem;
    width: 0; opacity: 0; padding: 0; border: 0 solid transparent;
    border-radius: 999px; background: #fff; overflow: hidden;
    transition: width .18s ease, opacity .12s ease, padding .18s ease;
  }
  .global-search.is-open .gs-field {
    width: 300px; opacity: 1; padding: .3rem .75rem; border-width: 1px; border-color: #dfe4ec;
  }
  @media (max-width: 1399px) { .global-search.is-open .gs-field { width: 220px; } }
  .global-search .gs-input {
    border: 0; outline: 0; background: transparent; width: 100%;
    font-size: .85rem; color: #212529; min-width: 0;
  }
  .global-search .gs-kbd {
    font-size: .62rem; font-weight: 700; color: #94a3b8;
    border: 1px solid #e2e8f0; border-radius: 4px; padding: .05rem .3rem; white-space: nowrap;
  }

  .global-search .gs-panel {
    position: absolute; top: calc(100% - 12px); right: 0; z-index: 1000;
    width: 380px; max-width: calc(100vw - 2rem); max-height: 60vh; overflow-y: auto;
    background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
    box-shadow: 0 10px 30px rgba(10, 49, 110, .12); padding: .4rem;
  }
  .global-search .gs-group-title {
    font-size: .62rem; text-transform: uppercase; letter-spacing: .4px;
    color: #94a3b8; font-weight: 700; padding: .5rem .65rem .25rem;
  }
  .global-search .gs-item {
    display: flex; align-items: center; gap: .6rem; width: 100%;
    padding: .5rem .65rem; border: 0; border-radius: 10px;
    background: transparent; text-align: left; color: #212529; font-size: .85rem;
  }
  .global-search .gs-item:hover, .global-search .gs-item.is-active { background: #f1f5fb; }
  .global-search .gs-item-icon {
    flex: 0 0 auto; width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(10, 49, 110, .08); color: ${NAVY};
  }
  .global-search .gs-item-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
  .global-search .gs-item-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .global-search .gs-item-sub {
    font-size: .72rem; color: #94a3b8; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .global-search .gs-lock { flex: 0 0 auto; color: #FFA500; }
  .global-search .gs-empty { padding: .9rem .65rem; color: #94a3b8; font-size: .82rem; }
`;

const GlobalSearch = () => {
    const navigate = useNavigate();
    const { roles } = useAuth();
    const { has: hasFeature, loaded: featuresLoaded, trialFor, trialDays, startTrial } = usePlanFeatures();

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [trialPrompt, setTrialPrompt] = useState(null);
    const [recentIds, setRecentIds] = useState(readRecent);
    const [records, setRecords] = useState([]);
    const [recordsLoading, setRecordsLoading] = useState(false);

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN");
    const isAccountUser = roles.some((role) => role.startsWith("ROLE_ACCOUNT_"));

    const targets = useMemo(
        () => filterByRoles(buildSearchTargets({ isSuperAdmin, isAccountUser }), roles),
        [isSuperAdmin, isAccountUser, roles]
    );

    /** Kutu boşken: son gidilenler, yoksa önerilenler. */
    const defaultResults = useMemo(() => {
        const recent = recentIds.map((id) => targets.find((t) => t.id === id)).filter(Boolean);
        if (recent.length > 0) return recent;
        return targets.filter((t) => t.suggested).slice(0, 5);
    }, [recentIds, targets]);

    const navItems = useMemo(() => {
        const list = query.trim() ? searchTargets(targets, query) : defaultResults;
        return list.map((target) => ({
            key: `nav-${target.id}`,
            label: target.label,
            subtitle: target.hint,
            icon: target.icon,
            group: target.group,
            target,
        }));
    }, [query, targets, defaultResults]);

    const recordItems = useMemo(
        () =>
            records.map((hit) => ({
                key: `${hit.type}-${hit.id}`,
                label: hit.title,
                subtitle: hit.subtitle,
                icon: RECORD_META[hit.type]?.icon || "file",
                group: hit.type,
                path: hit.path,
            })),
        [records]
    );

    const results = useMemo(() => [...navItems, ...recordItems], [navItems, recordItems]);

    const grouped = useMemo(() => {
        const order = [GROUP_PAGE, GROUP_ACTION, ...RECORD_ORDER];
        return order
            .map((group) => ({ group, items: results.filter((item) => item.group === group) }))
            .filter((row) => row.items.length > 0);
    }, [results]);

    const close = useCallback(() => {
        setOpen(false);
        setQuery("");
        setActiveIndex(0);
    }, []);

    const isLocked = useCallback(
        (target) => !!target.feature && featuresLoaded && !hasFeature(target.feature),
        [featuresLoaded, hasFeature]
    );

    const select = useCallback(
        (item) => {
            if (!item) return;

            /* Kayıt sonuçları "son gidilenler"e yazılmaz: liste sayfa kısayolları
               içindir, tek seferlik cari/ödeme kayıtları orayı doldurmasın. */
            if (!item.target) {
                close();
                navigate(item.path);
                return;
            }

            const target = item.target;
            const next = [target.id, ...recentIds.filter((id) => id !== target.id)].slice(0, RECENT_LIMIT);
            setRecentIds(next);
            writeRecent(next);

            if (isLocked(target)) {
                close();
                setTrialPrompt({ feature: target.feature, label: target.label, path: target.path });
                return;
            }

            close();
            navigate(target.path);
        },
        [close, isLocked, navigate, recentIds]
    );

    useEffect(() => setActiveIndex(0), [query]);

    /* Kayıt araması backend'e gider: her tuşta değil, yazma durunca. Eskiyen
       istek iptal edilir, yoksa geç dönen yanıt yeni sonucun üstüne yazabilir. */
    useEffect(() => {
        const term = query.trim();
        if (!open || term.length < MIN_QUERY) {
            setRecords([]);
            setRecordsLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setRecordsLoading(true);
        const timer = setTimeout(() => {
            searchPanel(term, { signal: controller.signal })
                .then((hits) => {
                    setRecords(hits);
                    setRecordsLoading(false);
                })
                .catch((err) => {
                    if (err.name === "AbortError") return;
                    setRecords([]);
                    setRecordsLoading(false);
                });
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query, open]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    useEffect(() => {
        const onKeyDown = (e) => {
            const isPaletteKey = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
            if (isPaletteKey) {
                e.preventDefault();
                setOpen((prev) => !prev);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            if (!containerRef.current?.contains(e.target)) close();
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [open, close]);

    const onInputKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            close();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            select(results[activeIndex]);
        }
    };

    const handleStartTrial = async () => {
        const target = trialPrompt;
        await startTrial(target.feature);
        setTrialPrompt(null);
        if (target.path) navigate(target.path);
    };

    let flatIndex = -1;

    return (
        <div className={`global-search${open ? " is-open" : ""}`} ref={containerRef}>
            <style>{styles}</style>

            <div className="gs-field">
                <FeatherIcon icon="search" size={15} style={{ color: "#94a3b8", flex: "0 0 auto" }} />
                <input
                    ref={inputRef}
                    className="gs-input"
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls="global-search-results"
                    aria-label="Panelde ara"
                    placeholder="Ara..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onInputKeyDown}
                    tabIndex={open ? 0 : -1}
                />
                <span className="gs-kbd">esc</span>
            </div>

            <button
                type="button"
                className="gs-toggle"
                title="Ara (⌘K)"
                aria-label="Ara"
                onClick={() => (open ? close() : setOpen(true))}
            >
                <FeatherIcon icon={open ? "x" : "search"} className="icon-lg" />
            </button>

            {open && (
                <div className="gs-panel" id="global-search-results" role="listbox">
                    {grouped.length === 0 && !recordsLoading && (
                        <div className="gs-empty">
                            "{query.trim()}" için sonuç yok. Sayfa adı ya da cari adı deneyin.
                        </div>
                    )}

                    {recordsLoading && (
                        <div className="gs-empty">Aranıyor…</div>
                    )}

                    {grouped.map(({ group, items }) => (
                        <div key={group}>
                            <div className="gs-group-title">
                                {RECORD_META[group]
                                    ? RECORD_META[group].label
                                    : query.trim()
                                        ? GROUP_LABELS[group]
                                        : recentIds.length > 0 ? "Son gidilenler" : "Öneriler"}
                            </div>
                            {items.map((item) => {
                                flatIndex += 1;
                                const index = flatIndex;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        role="option"
                                        aria-selected={index === activeIndex}
                                        className={`gs-item${index === activeIndex ? " is-active" : ""}`}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => select(item)}
                                    >
                                        <span className="gs-item-icon">
                                            <FeatherIcon icon={item.icon} size={15} />
                                        </span>
                                        <span className="gs-item-text">
                                            <span className="gs-item-label">{item.label}</span>
                                            {item.subtitle && <span className="gs-item-sub">{item.subtitle}</span>}
                                        </span>
                                        {item.target && isLocked(item.target) && (
                                            <FeatherIcon icon="lock" size={13} className="gs-lock" title="Paketinizde yok" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {trialPrompt && (
                <FeatureTrialModal
                    isOpen={true}
                    onClose={() => setTrialPrompt(null)}
                    feature={{ code: trialPrompt.feature, label: trialPrompt.label }}
                    trial={trialFor(trialPrompt.feature)}
                    trialDays={trialDays}
                    onStartTrial={handleStartTrial}
                    onUpgrade={() => {
                        setTrialPrompt(null);
                        navigate("/subscription/plans");
                    }}
                />
            )}
        </div>
    );
};

export default GlobalSearch;
